import React, { useState, useRef, useEffect, FormEvent, ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  MessageCircle,
  Briefcase,
  HeartHandshake,
  Store,
  ChevronDown,
} from "lucide-react";
import logo from "../assets/images/logo.png";
import heroImage from "../assets/images/hero-beach.png";
import { userService } from "../api/userService";
import { useUserStore, userToUserData } from "../stores/userStore";
import { useOnboardingStore } from "../stores/onboardingStore";
import config from "../config";

// `labelKey` (when present) is translated at render time; visa codes keep a static `label`.
const VISA_TYPES = [
  { value: "H2B", label: "H-2B" },
  { value: "J1", label: "J-1" },
  { value: "H1B", label: "H-1B" },
  { value: "L1", label: "L-1" },
  { value: "O1", label: "O-1" },
  { value: "F1", label: "F-1 / OPT" },
  { value: "TN", label: "TN" },
  { value: "E2", label: "E-2" },
  { value: "H2A", label: "H-2A" },
  { value: "B1B2", label: "B-1 / B-2" },
  { value: "Other", labelKey: "welcome.visa_type_other" },
];

const VISA_HISTORY_OPTIONS = [
  { value: "H2B", label: "H-2B" },
  { value: "J1", label: "J-1" },
  { value: "H1B", label: "H-1B" },
  { value: "OPT", label: "OPT" },
  { value: "TN", label: "TN" },
  { value: "L1", label: "L-1" },
  { value: "O1", label: "O-1" },
  { value: "never", labelKey: "welcome.visa_history_never" },
  { value: "prefer_not_to_say", labelKey: "welcome.visa_history_prefer_not" },
];

const WelcomeScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLang = (i18n.language || "en").startsWith("es") ? "es" : "en";
  const changeLang = (lang: "en" | "es") => {
    i18n.changeLanguage(lang);
    localStorage.setItem("vc_lang_v2", lang);
  };

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    visaType: "",
    memberType: "visa_worker" as "visa_worker" | "community_helper",
    visaHistory: "",
    helperStatus: "",
    dateOfBirth: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [tcError, setTcError] = useState(false);
  const [openTopic, setOpenTopic] = useState<number | null>(null);

  const signupFormRef = useRef<HTMLDivElement>(null);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [visaWorkers, setVisaWorkers] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${config.apiUrl}/api/stats/community`);
        if (!res.ok) return;
        const json = await res.json();
        if (!cancelled && json?.data?.visaWorkers != null) {
          setVisaWorkers(json.data.visaWorkers);
        }
      } catch {
        // leave null — copy falls back to a generic line
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowStickyBar(!entry.isIntersecting);
      },
      { threshold: 0.1 }
    );
    if (signupFormRef.current) {
      observer.observe(signupFormRef.current);
    }
    return () => observer.disconnect();
  }, []);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!agreedToTerms) {
      setTcError(true);
      return;
    }

    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim())
      newErrors.firstName = t("welcome.err_first_name");
    if (!formData.lastName.trim())
      newErrors.lastName = t("welcome.err_last_name");
    if (!formData.email.trim()) newErrors.email = t("welcome.err_email_required");
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = t("welcome.err_email_invalid");
    if (!formData.password) newErrors.password = t("welcome.err_password_required");
    else if (formData.password.length < 8)
      newErrors.password = t("welcome.err_password_short");
    if (formData.memberType === "visa_worker" && !formData.visaType) newErrors.visaType = t("welcome.err_visa_type");

    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = t("about_you.dob_required");
    } else {
      const dob = new Date(formData.dateOfBirth);
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const m = today.getMonth() - dob.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
      if (age < 18) newErrors.dateOfBirth = t("about_you.dob_under_18");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await userService.registerUser({
        ...formData,
        visaType: formData.memberType === "community_helper" ? "" : formData.visaType,
        confirmPassword: formData.password,
        location: "",
        dateOfBirth: formData.dateOfBirth,
        memberType: formData.memberType,
        visaHistory: formData.memberType === "community_helper" ? formData.visaHistory : undefined,
        helperStatus: formData.memberType === "community_helper" ? formData.helperStatus : undefined,
      });

      if (response.success && response.user && response.token) {
        const userData = userToUserData(response.user);
        const userStore = useUserStore.getState();
        userStore.setUser(userData);
        userStore.setToken(response.token);

        const onboardingStore = useOnboardingStore.getState();
        onboardingStore.setSignupData({
          ...formData,
          confirmPassword: formData.password,
          location: "",
        });

        toast.success(t("welcome.welcome_toast"));
        setTimeout(() => navigate("/optional-profile"), 400);
      } else {
        throw new Error(response.message || t("welcome.registration_failed"));
      }
    } catch (error: any) {
      let msg = t("welcome.generic_error");
      if (
        error.message?.includes("email") &&
        error.message?.includes("already")
      ) {
        setErrors({ email: t("welcome.email_already_registered") });
        msg = t("welcome.email_already_registered_full");
      } else if (error.message) {
        msg = error.message;
      }
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = (field: string) =>
    `w-full h-[50px] px-4 bg-[#f5f6f7] border text-[15px] text-gray-900 placeholder-gray-500 rounded-md outline-none transition-colors ${
      errors[field]
        ? "border-red-400 focus:border-red-500"
        : "border-[#dddfe2] focus:border-[#0085FF]"
    }`;

  return (
    <div className="min-h-screen">
      {/* ─── Nav ─── */}
      <nav className="lg:sticky lg:top-0 z-50 h-14 bg-[#111827] flex items-center justify-between px-5 sm:px-8">
        <img
          src={logo}
          alt="VisaConnect"
          className="h-5 w-auto max-w-[140px] brightness-0 invert opacity-90 cursor-pointer"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        />
        <div className="hidden md:flex items-center gap-6 text-[13px] font-medium text-gray-400">
          <button onClick={() => document.getElementById("pain-points")?.scrollIntoView({ behavior: "smooth" })} className="hover:text-white transition">{t("welcome.nav_why_us")}</button>
          <button onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })} className="hover:text-white transition">{t("welcome.nav_features")}</button>
          <button onClick={() => document.getElementById("community")?.scrollIntoView({ behavior: "smooth" })} className="hover:text-white transition">{t("welcome.nav_community")}</button>
        </div>
        <div className="flex items-center gap-2 text-[13px] font-semibold">
          <button onClick={() => changeLang("en")} className={currentLang === "en" ? "text-white" : "text-gray-500 hover:text-white transition"}>EN</button>
          <span className="text-gray-600">·</span>
          <button onClick={() => changeLang("es")} className={currentLang === "es" ? "text-white" : "text-gray-500 hover:text-white transition"}>ES</button>
        </div>
      </nav>

      {/* ═══════════ SECTION 1 — HERO ═══════════ */}
      <div className="flex flex-col lg:grid lg:grid-cols-[55%_1fr]" style={{ minHeight: "calc(100vh - 56px)" }}>
        {/* Left — Hero image with text overlay */}
        <div className="relative hidden lg:block lg:min-h-0 order-2 lg:order-1">
          <img
            src={heroImage}
            alt={t('alts.welcome_hero')}
            className="absolute inset-0 w-full h-full object-cover object-[center_25%]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 px-8 sm:px-10 lg:px-12 pb-8 lg:pb-12">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-4">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
              <span className="text-white text-sm font-medium">
                {visaWorkers != null
                  ? t("welcome.hero_badge_count", { count: visaWorkers })
                  : t("welcome.hero_badge")}
              </span>
            </div>
            <p className="text-[11px] font-bold uppercase tracking-widest text-[#0085FF] mb-3">
              {t("welcome.hero_eyebrow")}
            </p>
            <h1 className="text-[1.75rem] sm:text-[2rem] lg:text-[2.5rem] font-extrabold text-white leading-[1.15] tracking-tight">
              {t("welcome.hero_headline_1")}
              <br />
              {t("welcome.hero_headline_2")}
            </h1>
            <p className="mt-3 text-sm text-white/70 max-w-md">
              {t("welcome.hero_tagline")}
            </p>
          </div>
        </div>

        {/* Right — Form panel */}
        <div id="signup-section" ref={signupFormRef} className="bg-white flex items-center justify-center px-6 py-10 order-1 lg:order-2">
          <div className="w-full max-w-[396px]">
            <img src={logo} alt="VisaConnect" className="h-10 w-auto max-w-[260px] mx-auto mb-5" />
            <h2 className="text-xl font-bold text-gray-900 text-center">{t("welcome.form_heading")}</h2>
            <p className="mt-1 text-xs text-gray-400 text-center mb-6">{t("welcome.form_subtext")}</p>

            <form onSubmit={handleSubmit} className="space-y-2.5">
              <div className="grid grid-cols-2 gap-2.5">
                <div>
                  <input name="firstName" type="text" placeholder={t("welcome.ph_first_name")} value={formData.firstName} onChange={handleChange} className={inputClass("firstName")} />
                  {errors.firstName && <p className="mt-0.5 text-xs text-red-500">{errors.firstName}</p>}
                </div>
                <div>
                  <input name="lastName" type="text" placeholder={t("welcome.ph_last_name")} value={formData.lastName} onChange={handleChange} className={inputClass("lastName")} />
                  {errors.lastName && <p className="mt-0.5 text-xs text-red-500">{errors.lastName}</p>}
                </div>
              </div>
              <div>
                <input name="email" type="email" placeholder={t("welcome.ph_email")} value={formData.email} onChange={handleChange} className={inputClass("email")} />
                {errors.email && <p className="mt-0.5 text-xs text-red-500">{errors.email}</p>}
              </div>
              <div className="relative">
                <input name="password" type={showPassword ? "text" : "password"} placeholder={t("welcome.ph_password")} value={formData.password} onChange={handleChange} className={`${inputClass("password")} pr-14`} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400 hover:text-gray-600">{showPassword ? t("welcome.hide") : t("welcome.show")}</button>
                {errors.password && <p className="mt-0.5 text-xs text-red-500">{errors.password}</p>}
              </div>
              {/* Member type toggle */}
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, memberType: "visa_worker", visaHistory: "", helperStatus: "" }))}
                  className={`h-[50px] rounded-md border text-[13px] font-semibold transition-colors ${
                    formData.memberType === "visa_worker"
                      ? "border-[#0085FF] bg-blue-50 text-[#0085FF]"
                      : "border-[#dddfe2] bg-[#f5f6f7] text-gray-600 hover:border-gray-400"
                  }`}
                >
                  {t("welcome.toggle_on_visa")}
                </button>
                <button
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, memberType: "community_helper", visaType: "" }))}
                  className={`h-[50px] rounded-md border text-[13px] font-semibold transition-colors ${
                    formData.memberType === "community_helper"
                      ? "border-amber-500 bg-amber-50 text-amber-700"
                      : "border-[#dddfe2] bg-[#f5f6f7] text-gray-600 hover:border-gray-400"
                  }`}
                >
                  {t("welcome.toggle_here_to_help")}
                </button>
              </div>

              {/* Visa worker: visa type dropdown */}
              {formData.memberType === "visa_worker" && (
                <div>
                  <select name="visaType" value={formData.visaType} onChange={handleChange} className={`${inputClass("visaType")} ${!formData.visaType ? "text-gray-500" : ""}`}>
                    <option value="" disabled>{t("welcome.select_visa_type")}</option>
                    {VISA_TYPES.map((v) => (<option key={v.value} value={v.value}>{v.labelKey ? t(v.labelKey) : v.label}</option>))}
                  </select>
                  {errors.visaType && <p className="mt-0.5 text-xs text-red-500">{errors.visaType}</p>}
                </div>
              )}

              {/* Community helper: visa history + helper status */}
              {formData.memberType === "community_helper" && (
                <>
                  <div>
                    <select name="visaHistory" value={formData.visaHistory} onChange={handleChange} className={`${inputClass("visaHistory")} ${!formData.visaHistory ? "text-gray-500" : ""}`}>
                      <option value="" disabled>{t("welcome.select_visa_history")}</option>
                      {VISA_HISTORY_OPTIONS.map((v) => (<option key={v.value} value={v.value}>{v.labelKey ? t(v.labelKey) : v.label}</option>))}
                    </select>
                  </div>
                  <div>
                    <textarea
                      name="helperStatus"
                      value={formData.helperStatus}
                      onChange={handleChange}
                      maxLength={200}
                      placeholder={t("welcome.ph_helper_status")}
                      className={`${inputClass("helperStatus")} h-[60px] py-3 resize-none`}
                    />
                    <p className="mt-0.5 text-[10px] text-gray-400 text-right">{formData.helperStatus.length}/200</p>
                  </div>
                </>
              )}
              {/* Date of Birth */}
              <div>
                <label htmlFor="dob-landing" className="block text-xs font-medium text-gray-700 mb-1 text-left">
                  {t("about_you.date_of_birth")} <span className="text-red-500">*</span>
                </label>
                <input
                  id="dob-landing"
                  type="date"
                  max={(() => { const d = new Date(); d.setFullYear(d.getFullYear() - 18); return d.toISOString().split('T')[0]; })()}
                  value={formData.dateOfBirth}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, dateOfBirth: e.target.value }));
                    if (errors.dateOfBirth) setErrors(prev => { const n = { ...prev }; delete n.dateOfBirth; return n; });
                  }}
                  className={`w-full px-3.5 py-3 bg-gray-100 text-gray-900 text-sm rounded-md border ${
                    errors.dateOfBirth ? 'border-red-400' : 'border-transparent'
                  } focus:outline-none focus:ring-2 focus:ring-[#0085FF]`}
                />
                {errors.dateOfBirth && (
                  <p className="mt-1 text-xs text-red-500">{errors.dateOfBirth}</p>
                )}
              </div>

              <div className="flex items-start gap-3 pt-1">
                <input
                  type="checkbox"
                  id="terms-landing"
                  checked={agreedToTerms}
                  onChange={(e) => { setAgreedToTerms(e.target.checked); setTcError(false); }}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 cursor-pointer shrink-0"
                />
                <label htmlFor="terms-landing" className="text-xs text-gray-600 cursor-pointer">
                  {t("welcome.terms_agree")}{' '}
                  <a href="/#/terms" target="_blank" rel="noopener noreferrer" className="text-[#0085FF] hover:underline font-medium">{t("welcome.terms_conditions")}</a>
                  {' '}{t("welcome.terms_and")}{' '}
                  <a href="/#/privacy" target="_blank" rel="noopener noreferrer" className="text-[#0085FF] hover:underline font-medium">{t("welcome.privacy_policy")}</a>
                </label>
              </div>
              {tcError && (
                <p className="text-xs text-red-500 -mt-0.5">{t("welcome.terms_error")}</p>
              )}
              <button type="submit" disabled={isSubmitting} className="w-full h-[50px] mt-1 rounded-md bg-[#0085FF] hover:bg-[#0073E6] active:bg-[#0066CC] text-white text-[17px] font-bold transition-colors disabled:opacity-50">
                {isSubmitting ? t("welcome.creating_account") : t("welcome.join_visaconnect")}
              </button>
            </form>

            <p className="text-center text-[14px] text-gray-600 mt-5">
              {t("welcome.already_have_account")}{" "}
              <button onClick={() => navigate("/sign-in")} className="text-[#0085FF] font-semibold hover:underline">{t("welcome.sign_in")}</button>
            </p>

            {/* Trust signals */}
            <div className="mt-6 flex items-center justify-center gap-2 text-[11px] text-gray-400">
              <span>&#128274; {t("welcome.trust_private")}</span>
              <span>&middot;</span>
              <span>&#128101; {t("welcome.trust_real")}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════ SECTION 2 — PAIN POINTS ═══════════ */}
      <section id="pain-points" className="bg-[#0F172A] py-20 sm:py-24">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
              {t("welcome.pain_heading")}
            </h2>
            <p className="mt-3 text-lg text-gray-400 max-w-2xl mx-auto">
              {t("welcome.pain_subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {[
              {
                emoji: "\uD83C\uDFE6",
                title: t("welcome.pain_credit_title"),
                desc: t("welcome.pain_credit_desc"),
              },
              {
                emoji: "\uD83C\uDFE0",
                title: t("welcome.pain_housing_title"),
                desc: t("welcome.pain_housing_desc"),
              },
              {
                emoji: "\uD83D\uDCBC",
                title: t("welcome.pain_jobs_title"),
                desc: t("welcome.pain_jobs_desc"),
              },
              {
                emoji: "\uD83D\uDDE3\uFE0F",
                title: t("welcome.pain_nobody_title"),
                desc: t("welcome.pain_nobody_desc"),
              },
              {
                emoji: "\uD83D\uDE14",
                title: t("welcome.pain_lonely_title"),
                desc: t("welcome.pain_lonely_desc"),
              },
            ].map((card) => (
              <div
                key={card.title}
                className="bg-white/5 rounded-xl p-6 border border-gray-200"
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-xl">{card.emoji}</span>
                  <h3 className="font-bold text-white text-[16px]">{card.title}</h3>
                </div>
                <p className="text-[14px] text-gray-400 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ SECTION — WHAT YOU NEED TO KNOW ═══════════ */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-14">
            <p className="text-[#0085FF] text-xs font-bold uppercase tracking-widest mb-3">
              {t("welcome.know_eyebrow")}
            </p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
              {t("welcome.know_heading")}
            </h2>
            <p className="mt-3 text-lg text-gray-500 max-w-2xl mx-auto">
              {t("welcome.know_subtitle")}
            </p>
          </div>

          {/* Accordion */}
          <div className="space-y-3">
            {[
              {
                emoji: "\uD83D\uDCCA",
                title: t("welcome.acc_credit_title"),
                content: (
                  <>
                    <p className="mb-3">{t("welcome.acc_credit_p1")}</p>
                    <p className="font-semibold mb-1">{t("welcome.acc_credit_h1")}</p>
                    <ul className="list-disc list-inside mb-3 space-y-1">
                      <li>{t("welcome.acc_credit_l1_1")}</li>
                      <li>{t("welcome.acc_credit_l1_2")}</li>
                      <li>{t("welcome.acc_credit_l1_3")}</li>
                      <li>{t("welcome.acc_credit_l1_4")}</li>
                      <li>{t("welcome.acc_credit_l1_5")}</li>
                    </ul>
                    <p className="font-semibold mb-1">{t("welcome.acc_credit_h2")}</p>
                    <ul className="list-disc list-inside mb-3 space-y-1">
                      <li>{t("welcome.acc_credit_l2_1")}</li>
                      <li>{t("welcome.acc_credit_l2_2")}</li>
                      <li>{t("welcome.acc_credit_l2_3")}</li>
                    </ul>
                    <p className="text-xs text-gray-400 mt-2">{t("welcome.acc_credit_source")}</p>
                  </>
                ),
              },
              {
                emoji: "\uD83E\uDEAA",
                title: t("welcome.acc_ssn_title"),
                content: (
                  <>
                    <p className="mb-3">{t("welcome.acc_ssn_p1")}</p>
                    <p className="mb-1"><span className="font-semibold">{t("welcome.acc_ssn_eligible_label")}</span> H-2B, H-1B, J-1, TN, O-1, L-1</p>
                    <p className="mb-3"><span className="font-semibold">{t("welcome.acc_ssn_not_eligible_label")}</span> H-4 dependents, F-2, TD, O-3</p>
                    <p className="font-semibold mb-1">{t("welcome.acc_ssn_h1")}</p>
                    <ol className="list-decimal list-inside mb-3 space-y-1">
                      <li>{t("welcome.acc_ssn_l1")}</li>
                      <li>{t("welcome.acc_ssn_l2")}</li>
                      <li>{t("welcome.acc_ssn_l3")}</li>
                      <li>{t("welcome.acc_ssn_l4")}</li>
                      <li>{t("welcome.acc_ssn_l5")}</li>
                    </ol>
                    <p className="mb-2"><span className="font-semibold">{t("welcome.acc_ssn_important_label")}</span> {t("welcome.acc_ssn_important")}</p>
                    <p className="text-xs text-gray-400 mt-2">{t("welcome.acc_ssn_source")}</p>
                  </>
                ),
              },
              {
                emoji: "\uD83D\uDE97",
                title: t("welcome.acc_dl_title"),
                content: (
                  <>
                    <p className="mb-3">{t("welcome.acc_dl_p1")}</p>
                    <p className="font-semibold mb-1">{t("welcome.acc_dl_h1")}</p>
                    <ul className="list-disc list-inside mb-3 space-y-1">
                      <li>{t("welcome.acc_dl_l1_1")}</li>
                      <li>{t("welcome.acc_dl_l1_2")}</li>
                      <li>{t("welcome.acc_dl_l1_3")}</li>
                      <li>{t("welcome.acc_dl_l1_4")}</li>
                      <li>{t("welcome.acc_dl_l1_5")}</li>
                    </ul>
                    <p className="font-semibold mb-1">{t("welcome.acc_dl_h2")}</p>
                    <ol className="list-decimal list-inside mb-3 space-y-1">
                      <li>{t("welcome.acc_dl_l2_1")}</li>
                      <li>{t("welcome.acc_dl_l2_2")}</li>
                      <li>{t("welcome.acc_dl_l2_3")}</li>
                      <li>{t("welcome.acc_dl_l2_4")}</li>
                    </ol>
                    <p className="mb-2"><span className="font-semibold">{t("welcome.acc_dl_mistake_label")}</span> {t("welcome.acc_dl_mistake")}</p>
                    <p className="text-xs text-gray-400 mt-2">{t("welcome.acc_dl_source")}</p>
                  </>
                ),
              },
              {
                emoji: "\uD83C\uDFE5",
                title: t("welcome.acc_health_title"),
                content: (
                  <>
                    <p className="mb-3">{t("welcome.acc_health_p1")}</p>
                    <p className="mb-1"><span className="font-semibold">{t("welcome.acc_health_premium_label")}</span> {t("welcome.acc_health_premium")}</p>
                    <p className="mb-1"><span className="font-semibold">{t("welcome.acc_health_deductible_label")}</span> {t("welcome.acc_health_deductible")}</p>
                    <p className="mb-1"><span className="font-semibold">{t("welcome.acc_health_copay_label")}</span> {t("welcome.acc_health_copay")}</p>
                    <p className="mb-3"><span className="font-semibold">{t("welcome.acc_health_coinsurance_label")}</span> {t("welcome.acc_health_coinsurance")}</p>
                    <p className="font-semibold mb-1">{t("welcome.acc_health_h1")}</p>
                    <ul className="list-disc list-inside mb-3 space-y-1">
                      <li>{t("welcome.acc_health_l1")}</li>
                      <li>{t("welcome.acc_health_l2")}</li>
                      <li>{t("welcome.acc_health_l3")}</li>
                    </ul>
                    <p className="text-xs text-gray-400 mt-2">{t("welcome.acc_health_source")}</p>
                  </>
                ),
              },
              {
                emoji: "\uD83D\uDCB8",
                title: t("welcome.acc_money_title"),
                content: (
                  <>
                    <p className="mb-3">{t("welcome.acc_money_p1")}</p>
                    <p className="font-semibold mb-1">{t("welcome.acc_money_wise_h")}</p>
                    <ul className="list-disc list-inside mb-3 space-y-1">
                      <li>{t("welcome.acc_money_wise_l1")}</li>
                      <li>{t("welcome.acc_money_wise_l2")}</li>
                      <li>{t("welcome.acc_money_wise_l3")}</li>
                      <li>{t("welcome.acc_money_wise_l4")}</li>
                      <li>{t("welcome.acc_money_wise_l5")}</li>
                    </ul>
                    <p className="font-semibold mb-1">{t("welcome.acc_money_remitly_h")}</p>
                    <ul className="list-disc list-inside mb-3 space-y-1">
                      <li>{t("welcome.acc_money_remitly_l1")}</li>
                      <li>{t("welcome.acc_money_remitly_l2")}</li>
                      <li>{t("welcome.acc_money_remitly_l3")}</li>
                      <li>{t("welcome.acc_money_remitly_l4")}</li>
                      <li>{t("welcome.acc_money_remitly_l5")}</li>
                    </ul>
                    <p className="mb-2"><span className="font-semibold">{t("welcome.acc_money_rule_label")}</span> {t("welcome.acc_money_rule")}</p>
                    <p className="text-xs text-gray-400 mt-2">{t("welcome.acc_money_source")}</p>
                  </>
                ),
              },
            ].map((topic, idx) => (
              <div key={idx} className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenTopic(openTopic === idx ? null : idx)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{topic.emoji}</span>
                    <span className="font-bold text-gray-900 text-[15px] sm:text-[16px]">{topic.title}</span>
                  </div>
                  <ChevronDown
                    className={`h-5 w-5 text-gray-400 shrink-0 transition-transform duration-200 ${
                      openTopic === idx ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {openTopic === idx && (
                  <div className="px-6 py-5 text-[14px] text-gray-600 leading-relaxed">
                    {topic.content}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Q&A Prompts */}
          <div className="mt-16 text-center">
            <h3 className="text-xl font-bold text-gray-900">{t("welcome.qa_heading")}</h3>
            <p className="mt-1 text-sm text-gray-500">
              {t("welcome.qa_subtitle")}
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              {
                question: "\uD83C\uDF34 " + t("welcome.qa_card_1"),
              },
              {
                question: "\uD83C\uDFD6\uFE0F " + t("welcome.qa_card_2"),
              },
              {
                question: "\uD83C\uDFE6 " + t("welcome.qa_card_3"),
              },
            ].map((card, idx) => (
              <div
                key={idx}
                className="bg-[#0F172A] border border-white/10 rounded-xl p-5 flex flex-col justify-between"
              >
                <p className="text-white text-[15px] font-medium leading-snug mb-4">
                  {card.question}
                </p>
                <button
                  onClick={() => window.location.href = '/#/community/need-help'}
                  className="self-start px-4 py-2 bg-[#0085FF] hover:bg-[#0073E6] text-white text-sm font-semibold rounded-lg transition"
                >
                  {t("welcome.ask_community")} &rarr;
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ SECTION 3 — HOW IT WORKS / FEATURES ═══════════ */}
      <section id="features" className="bg-white py-20 sm:py-24">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
              {t("welcome.features_heading")}
            </h2>
            <p className="mt-3 text-lg text-gray-500 max-w-2xl mx-auto">
              {t("welcome.features_subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {[
              {
                Icon: MessageCircle,
                gradient: "from-sky-500 to-blue-600",
                title: t("welcome.feature_feed_title"),
                text: t("welcome.feature_feed_text"),
              },
              {
                Icon: Briefcase,
                gradient: "from-violet-500 to-purple-600",
                title: t("welcome.feature_jobs_title"),
                text: t("welcome.feature_jobs_text"),
              },
              {
                Icon: HeartHandshake,
                gradient: "from-amber-500 to-orange-600",
                title: t("welcome.feature_help_title"),
                text: t("welcome.feature_help_text"),
              },
              {
                Icon: Store,
                gradient: "from-emerald-500 to-teal-600",
                title: t("welcome.feature_reviews_title"),
                text: t("welcome.feature_reviews_text"),
              },
            ].map((card) => (
              <div
                key={card.title}
                className="group rounded-2xl bg-white p-7 border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              >
                <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${card.gradient} flex items-center justify-center mb-5 shadow-md group-hover:scale-110 transition-transform duration-300`}>
                  <card.Icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-bold text-[17px] text-gray-900 mb-2">{card.title}</h3>
                <p className="text-[14px] text-gray-500 leading-relaxed">{card.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ SECTION 4 — SOCIAL PROOF / COMMUNITY ═══════════ */}
      <section id="community" className="relative bg-[#111827] py-20 sm:py-24 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-indigo-500/10 rounded-full blur-3xl" />

        <div className="relative max-w-4xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
              {t("welcome.social_heading")}
            </h2>
          </div>

          <div className="max-w-2xl mx-auto">
            {/* Raphael Sagna */}
            <div className="flex items-start gap-4 bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/10 transition-colors">
              <img
                src="https://storage.googleapis.com/visaconnectus.firebasestorage.app/profile-photos/VCx2ucYRYoexYucPrfRYzv8ZEWq1_1762444541441.jpeg"
                alt="Raphael Sagna"
                className="w-12 h-12 rounded-full object-cover"
              />
              <div>
                <p className="font-semibold text-white text-[15px]">&ldquo;{t("welcome.founder_quote")}&rdquo;</p>
                <p className="mt-1.5 text-sm text-gray-400">{t("welcome.founder_attribution")}</p>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ═══════════ SECTION 5 — FOR EMPLOYERS ═══════════ */}
      <section className="bg-[#F8FAFC] py-20 sm:py-24 border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            {/* Left — Value proposition */}
            <div>
              <span className="text-[#0085FF] text-xs font-bold uppercase tracking-widest">
                {t("welcome.employers_eyebrow")}
              </span>
              <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight leading-tight">
                {t("welcome.employers_headline")}
              </h2>
              <p className="mt-4 text-gray-500 text-[16px] leading-relaxed max-w-lg">
                {t("welcome.employers_desc")}
              </p>

              <div className="mt-8 space-y-4">
                {[
                  t("welcome.employers_benefit_1"),
                  t("welcome.employers_benefit_2"),
                  t("welcome.employers_benefit_3"),
                ].map((benefit) => (
                  <div key={benefit} className="flex items-start gap-3">
                    <svg
                      className="h-5 w-5 text-[#0085FF] shrink-0 mt-0.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="text-gray-700 text-[15px] leading-snug">
                      {benefit}
                    </span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => navigate("/employer/register")}
                className="mt-8 px-7 py-3.5 bg-[#0085FF] hover:bg-[#0073E6] text-white text-[15px] font-bold rounded-lg transition shadow-md active:scale-[0.97]"
              >
                {t("welcome.register_employer")}
              </button>
            </div>

            {/* Right — Stat card */}
            <div className="bg-[#0A1937] rounded-2xl p-8 sm:p-10 text-white">
              <div className="mb-1">
                <span className="text-4xl sm:text-5xl font-extrabold">
                  {visaWorkers != null ? `${visaWorkers}+` : "—"}
                </span>
              </div>
              <p className="text-sm text-gray-400 font-medium">
                {t("welcome.stat_workers_label")}
              </p>

              <p className="mt-4 text-sm text-gray-300 font-medium tracking-wide">
                H-2B &middot; J-1 &middot; H-1B &middot; L-1 &middot; OPT
              </p>

              <span className="inline-block mt-4 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-xs font-bold">
                {t("welcome.stat_free_to_post")}
              </span>

              <div className="my-6 border-t border-white/10" />

              <p className="text-white text-[15px] font-semibold">
                {t("welcome.stat_growing")}
              </p>

              <div className="mt-6 border-t border-white/10 pt-6">
                <p className="text-sm text-gray-400 italic leading-relaxed">
                  {t("welcome.stat_partner")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ SECTION 6 — BOTTOM CTA ═══════════ */}
      <section className="bg-[#111827] py-16 sm:py-20">
        <div className="max-w-2xl mx-auto px-5 text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
            {t("welcome.cta_heading")}
          </h2>
          <p className="mt-3 text-gray-400 text-[15px]">
            {t("welcome.cta_subtitle")}
          </p>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="mt-8 px-8 py-4 bg-[#0085FF] hover:bg-[#0073E6] text-white text-[16px] font-bold rounded-lg transition shadow-lg shadow-blue-500/20 active:scale-[0.97]"
          >
            {t("welcome.cta_button")} &rarr;
          </button>
        </div>
      </section>

      {/* ═══════════ FOOTER ═══════════ */}
      <footer className="bg-[#0d1117] py-12 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Top row */}
          <div className="flex flex-col md:flex-row justify-between items-start gap-10 mb-10">

            {/* Brand */}
            <div className="max-w-xs">
              <div className="text-white font-bold text-lg mb-2">VisaConnect</div>
              <p className="text-gray-500 text-sm leading-relaxed">
                {t("welcome.footer_tagline")}
              </p>
              <p className="text-gray-600 text-xs mt-4">
                H-2B &middot; J-1 &middot; H-1B &middot; L-1 &middot; O-1 &middot; OPT &middot; TN
              </p>
            </div>

            {/* Links */}
            <div className="flex flex-wrap gap-16">
              <div>
                <div className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">{t("welcome.footer_platform")}</div>
                <ul className="space-y-2">
                  <li><a href="/dashboard" className="text-gray-500 hover:text-white text-sm transition">{t("welcome.footer_dashboard")}</a></li>
                  <li><a href="/search-jobs" className="text-gray-500 hover:text-white text-sm transition">{t("welcome.footer_jobs")}</a></li>
                  <li><a href="/community/feed" className="text-gray-500 hover:text-white text-sm transition">{t("welcome.footer_community")}</a></li>
                  <li><a href="/employer/register" className="text-gray-500 hover:text-white text-sm transition">{t("welcome.footer_for_employers")}</a></li>
                </ul>
              </div>
              <div>
                <div className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">{t("welcome.footer_contact")}</div>
                <ul className="space-y-2">
                  <li>
                    <a
                      href="mailto:raphael@visaconnectus.com"
                      className="text-gray-500 hover:text-white text-sm transition"
                    >
                      raphael@visaconnectus.com
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://www.instagram.com/visa_connectus"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 hover:text-white text-sm transition"
                    >
                      @visa_connectus
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://www.instagram.com/raphael_sagna"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 hover:text-white text-sm transition"
                    >
                      @raphael_sagna
                    </a>
                  </li>
                </ul>
              </div>
              <div>
                <div className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">{t("welcome.footer_legal")}</div>
                <ul className="space-y-2">
                  <li><a href="/privacy" className="text-gray-500 hover:text-white text-sm transition">{t("welcome.privacy_policy")}</a></li>
                  <li><a href="/terms" className="text-gray-500 hover:text-white text-sm transition">{t("welcome.terms_conditions")}</a></li>
                </ul>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-800 pt-6 flex flex-col md:flex-row justify-between items-center gap-3">
            <p className="text-gray-600 text-xs">
              &copy; {new Date().getFullYear()} VisaConnect &mdash; {t("welcome.footer_copyright")}
            </p>
            <p className="text-gray-700 text-xs">
              {t("welcome.footer_built_by")}
            </p>
          </div>
        </div>
      </footer>

      {/* ─── Mobile Sticky CTA ─── */}
      {showStickyBar && (
        <div className="fixed bottom-0 inset-x-0 p-3 bg-white border-t border-gray-200 lg:hidden z-50">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="w-full h-12 bg-[#0085FF] hover:bg-[#0073E6] text-white font-bold rounded-lg transition active:scale-[0.98]"
          >
            {t("welcome.join_visaconnect")}
          </button>
        </div>
      )}
    </div>
  );
};

export default WelcomeScreen;
